import React, { useState, useEffect } from 'react';
import { Calendar, RefreshCw, AlertTriangle, Info, ArrowLeft, CheckCircle } from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';

const ReportAnnuel = () => {
  const { user } = useAuth();
  const { filialeId, isConsolidated } = useFiliale();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const params = {};
      if (!isConsolidated && filialeId) {
        params.filiale_id = filialeId;
      }
      const response = await http.get('/api/conges/report-annuel/stats', { params });
      setStats(response.data);
    } catch (error) {
      console.error('Erreur récupération stats:', error);
      setError('Erreur lors de la récupération des statistiques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [filialeId, isConsolidated]);

  const handleReport = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir effectuer le report annuel ? Cette action sera enregistrée dans l\'historique et est irréversible.')) {
      return;
    }

    try {
      setProcessing(true);
      const params = {};
      if (!isConsolidated && filialeId) {
        params.filiale_id = filialeId;
      }
      const response = await http.post('/api/conges/report-annuel', params);
      setSuccess(true);
      setError(null);
      setTimeout(() => {
        fetchStats();
        setSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Erreur report annuel:', error);
      setError(error.response?.data?.error || 'Erreur lors du report annuel');
      setSuccess(false);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex h-48 items-center justify-center text-sm text-slate-500">
          Chargement…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Report Annuel des Soldes de Congé"
        subtitle="Reporter 100% du solde de congé d'une année à l'autre"
        showBack={true}
        onBack={() => window.history.back()}
        actions={[
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: fetchStats,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8 space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <p className="text-sm text-green-800">
              Report annuel effectué avec succès : {stats?.employes_concernes} employés actifs traités, total de {parseFloat(stats?.total_solde || 0).toFixed(1)} jours reportés
            </p>
          </div>
        </div>
      )}

      {/* Information */}
      <div className="bg-white border-l-4 border-sky-500 rounded-2xl p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-sky-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-slate-900 mb-2">Information</h4>
            <p className="text-sm text-slate-700 mb-1">
              Le report annuel permet de conserver 100% du solde de congé de chaque employé contractuel actif d'une année à l'autre.
            </p>
            <p className="text-sm text-slate-700 mb-1">
              Cette action sera enregistrée dans l'historique pour traçabilité.
            </p>
            <p className="text-sm text-slate-700 font-semibold">
              Important : Seuls les employés actifs (active = 1) sont concernés par ce report.
            </p>
          </div>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-white border-l-4 border-red-500 rounded-2xl p-5 shadow-sm">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-slate-900 mb-2">Attention</h4>
            <p className="text-sm text-slate-700">
              Le report annuel est irréversible. Assurez-vous de vouloir effectuer cette action.
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-slate-500 mb-2">Année précédente</div>
            <div className="text-2xl font-bold text-slate-900">{stats.annee_precedente}</div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-slate-500 mb-2">Année courante</div>
            <div className="text-2xl font-bold text-slate-900">{stats.annee_courante}</div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-slate-500 mb-2">Employés concernés</div>
            <div className="text-2xl font-bold text-slate-900">{stats.employes_concernes || 0}</div>
            <div className="text-xs text-slate-500 mt-1">Personnel contractuel actif (CDD, CDI)</div>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5">
            <div className="text-sm text-slate-500 mb-2">Total des jours à reporter</div>
            <div className="text-2xl font-bold text-slate-900">{parseFloat(stats.total_solde || 0).toFixed(1)} jours</div>
            <div className="text-xs text-slate-500 mt-1">Solde cumulé de tous les employés</div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          onClick={handleReport}
          disabled={processing || !stats || stats.employes_concernes === 0}
          className="px-6 py-3 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {processing ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin" />
              Traitement en cours...
            </>
          ) : (
            <>
              <AlertTriangle className="h-4 w-4" />
              Confirmer le Report Annuel
            </>
          )}
        </button>
        <button
          onClick={() => window.history.back()}
          className="px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Annuler
        </button>
      </div>
      </div>
    </div>
  );
};

export default ReportAnnuel;
